import { useParams, Link, Navigate } from "react-router-dom";
import { ShieldCheck, Factory, Clock, Truck } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import { getStateBySlug, getCitiesByState } from "../../data/locations";
import { business } from "../../config/business";

const StatePage = () => {
  const { stateSlug } = useParams();
  const state = getStateBySlug(stateSlug);

  if (!state) return <Navigate to="/locations" replace />;

  const cities = getCitiesByState(state.slug);

  return (
    <Layout>
      <SEOHead
        title={`PEB Manufacturer in ${state.name} | Vasavi Structures`}
        description={`Vasavi Structures designs, fabricates and delivers PEB buildings, industrial sheds and steel structures across ${state.name} from our Hyderabad facility.`}
        keywords={`PEB manufacturer ${state.name}, industrial shed manufacturer ${state.name}, steel fabrication ${state.name}, PEB structures ${state.name}`}
      />
      <Breadcrumb items={[{ name: "Locations", path: "/locations" }, { name: state.name }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-10">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            PEB & Industrial Structures in <span className="text-secondary">{state.name}</span>
          </h1>
          <p className="text-muted-foreground max-w-3xl text-lg">{state.intro}</p>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast fabrication-to-erection timelines</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Statewide delivery & erection</span>
          </div>
        </div>
      </section>

      {/* 3. Cities grid */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6">
            Cities We Serve in {state.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((city) => (
              <Link
                key={city.slug}
                to={`/locations/${state.slug}/${city.slug}`}
                className="p-5 bg-card rounded-xl shadow-md card-hover border border-border"
              >
                <h3 className="font-montserrat font-bold text-foreground mb-1">{city.name}</h3>
                <p className="text-sm text-muted-foreground">{city.distance}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Services grid */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">
            Our Services in {state.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "PEB Buildings", path: "/pre-fab-structure-buildings" },
              { name: "Industrial Sheds", path: "/industrial-sheds" },
              { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
              { name: "Roof & Wall Sheeting", path: "/roof-wall-sheeting" },
            ].map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="p-4 bg-card rounded-lg text-center font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition shadow-sm"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Vasavi */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-8 text-center">
            Why Businesses in {state.name} Choose Vasavi Structures
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Factory, title: "Single Point of Fabrication", text: "One facility, consistent quality control across every project." },
              { icon: ShieldCheck, title: "Code-Compliant Design", text: "Structures engineered to site-specific wind and seismic requirements." },
              { icon: Clock, title: "Faster Than RCC", text: "Parallel fabrication and foundation work compress project timelines." },
              { icon: Truck, title: "End-to-End Delivery", text: "Transport and erection managed through to handover." },
            ].map((item) => (
              <div key={item.title} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="text-primary" size={22} aria-hidden="true" />
                </div>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Get a Quote for Your {state.name} Project
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Share your requirements and our engineering team will get back with a design consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-outline border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary inline-block px-8 py-4">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default StatePage;
